import React, { useMemo, useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { LineChart, Line, AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { statisticsService } from '../../services/statisticsService';
import { DailyActivity } from '../../types';
import { getHunterClass, getClassMotivation, extractRankFromTitle } from '../../utils/hunterClassification';

const StatsPage: React.FC = () => {
  const { profile, quests, getEffectiveStats } = useUser();
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState<7 | 30>(7);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 640px)');
    const compute = () => setIsSmallScreen(!mql.matches);
    compute();
    if (mql.addEventListener) {
      mql.addEventListener('change', compute);
      return () => mql.removeEventListener('change', compute);
    } else {
      // Safari fallback
      // @ts-ignore
      mql.addListener(compute);
      return () => {
        // @ts-ignore
        mql.removeListener(compute);
      };
    }
  }, []);

  const completedQuests = quests.filter(q => q.status === 'completed');

  const dailyStats = useMemo(() => {
    return statisticsService.getLastNDays(timeRange);
  }, [timeRange]);

  // Use lifetime stats from profile (persists even after clearing history)
  const totals = useMemo(() => {
    if (!profile?.lifetimeStats) {
      return {
        totalQuests: 0,
        totalXP: 0,
        totalCurrency: 0,
      };
    }
    
    return {
      totalQuests: profile.lifetimeStats.totalQuestsCompleted,
      totalXP: profile.lifetimeStats.totalXPEarned,
      totalCurrency: profile.lifetimeStats.totalCurrencyEarned,
    };
  }, [profile]);

  const chartData = dailyStats.map((d: DailyActivity) => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    'Quests': d.questsCompleted,
    'XP': d.xpEarned,
    'Focus Time': d.focusMinutes,
    'Currency': d.currencyEarned,
  }));

  // Radar chart data for player stats (very Solo Leveling!) - with passive boosts applied
  const effectiveStats = getEffectiveStats();
  
  // Determine Hunter Class based on stat distribution
  const hunterClass = useMemo(() => {
    return getHunterClass(effectiveStats);
  }, [effectiveStats]);
  
  // Extract user's actual rank from their title
  const userRank = profile ? extractRankFromTitle(profile.currentTitle) : 'E';
  
  const classMotivation = getClassMotivation(hunterClass.tier);
  
  // Calculate dynamic max for radar chart (scales with player growth)
  const maxStatValue = profile ? Math.max(
    effectiveStats.strength,
    effectiveStats.intelligence,
    effectiveStats.agility,
    effectiveStats.vitality,
    effectiveStats.sense
  ) : 100;
  
  // Round up to nearest 50 for a clean scale (min 100)
  const radarMaxDomain = Math.max(100, Math.ceil(maxStatValue / 50) * 50);
  
  const statsRadarData = profile ? [
    { stat: 'STR', value: effectiveStats.strength, max: radarMaxDomain },
    { stat: 'INT', value: effectiveStats.intelligence, max: radarMaxDomain },
    { stat: 'AGI', value: effectiveStats.agility, max: radarMaxDomain },
    { stat: 'VIT', value: effectiveStats.vitality, max: radarMaxDomain },
    { stat: 'SENSE', value: effectiveStats.sense, max: radarMaxDomain },
  ] : [];
  
  if (!profile) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-400">{t.common.loading}</div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gradient-to-br from-system-dark to-system-gray border-2 border-system-blue rounded-lg p-3 font-rajdhani shadow-neon-blue">
          <p className="text-white font-bold mb-2 font-orbitron text-sm">{payload[0].payload.date}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
              <span className="text-gray-300">{entry.name}:</span>
              <span className="font-bold" style={{ color: entry.color }}>{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="animate-fade-in max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse"></div>
            <span className="font-orbitron text-2xl tracking-wider text-system-blue uppercase">
              ［ {t.stats.title} ］
            </span>
          </div>
          <p className="text-sm text-gray-400 font-rajdhani">
            {t.stats.subtitle}
          </p>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setTimeRange(7)}
          className={`px-4 py-2 rounded-2xl font-orbitron font-semibold text-sm tracking-wider transition-all relative overflow-hidden ${
            timeRange === 7
              ? 'bg-system-blue text-white hover:scale-105 active:scale-95'
              : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 text-gray-400 hover:text-white hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70'
          }`}
          style={timeRange === 7 ? {
            borderTop: '2px solid rgba(255, 255, 255, 0.3)',
            borderLeft: '2px solid rgba(255, 255, 255, 0.3)',
            borderRight: '2px solid rgba(0, 0, 0, 0.4)',
            borderBottom: '2px solid rgba(0, 0, 0, 0.4)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.2), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
          } : {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
          }}
        >
          {timeRange === 7 && (
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
          )}
          <span className="relative">{t.stats.days7}</span>
        </button>
        <button
          onClick={() => setTimeRange(30)}
          className={`px-4 py-2 rounded-2xl font-orbitron font-semibold text-sm tracking-wider transition-all relative overflow-hidden ${
            timeRange === 30
              ? 'bg-system-blue text-white hover:scale-105 active:scale-95'
              : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 text-gray-400 hover:text-white hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70'
          }`}
          style={timeRange === 30 ? {
            borderTop: '2px solid rgba(255, 255, 255, 0.3)',
            borderLeft: '2px solid rgba(255, 255, 255, 0.3)',
            borderRight: '2px solid rgba(0, 0, 0, 0.4)',
            borderBottom: '2px solid rgba(0, 0, 0, 0.4)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.2), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
          } : {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
          }}
        >
          {timeRange === 30 && (
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
          )}
          <span className="relative">{t.stats.days30}</span>
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1 flex items-center gap-1">
            <span>🎖️</span>
            <span>{t.stats.level}</span>
          </div>
          <div className="text-3xl font-black font-orbitron glow-text">{profile.level}</div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 hover:border-t-green-400/70 hover:border-l-green-400/70 hover:border-r-green-600/80 hover:border-b-green-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1 flex items-center gap-1">
            <span>✓</span>
            <span>{t.stats.quests}</span>
          </div>
          <div className="text-3xl font-black font-orbitron text-green-400">{totals.totalQuests}</div>
          <div className="text-[10px] text-gray-500 mt-1">History: {completedQuests.length}</div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 hover:border-t-purple-400/70 hover:border-l-purple-400/70 hover:border-r-purple-600/80 hover:border-b-purple-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1 flex items-center gap-1">
            <span>⚡</span>
            <span>Lifetime XP</span>
          </div>
          <div className="text-3xl font-black font-orbitron text-purple-400">{totals.totalXP}</div>
          <div className="text-[10px] text-gray-500 mt-1">Level {profile.level} • {profile.currentXP}/{profile.xpToNextLevel} XP</div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 hover:border-t-yellow-400/70 hover:border-l-yellow-400/70 hover:border-r-yellow-600/80 hover:border-b-yellow-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1 flex items-center gap-1">
            <span>💰</span>
            <span>{t.stats.currentCurrency}</span>
          </div>
          <div className="text-3xl font-black font-orbitron" style={{ color: '#FCD34D' }}>{profile.currency}</div>
          <div className="text-[10px] text-gray-500 mt-1">
            Lifetime Earned: {totals.totalCurrency}
          </div>
        </div>
      </div>

      {/* Player Stats Radar Chart - SOLO LEVELING STYLE! */}
      <div 
        className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 mb-6 transition-all"
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold font-orbitron text-system-blue flex items-center gap-2">
            <span className="text-2xl">📡</span>
            <span>{t.stats.hunterStatsAnalysis}</span>
          </h3>
          <div className="flex items-center gap-3">
            {radarMaxDomain > 100 && (
              <div className="text-xs font-rajdhani text-purple-400 bg-purple-900/30 border border-purple-500/50 rounded px-2 py-1">
                SCALE: 0-{radarMaxDomain}
              </div>
            )}
            <div className="text-xs text-gray-400 font-rajdhani uppercase">
              {t.stats.systemInterface}
            </div>
          </div>
        </div>

        {/* Hunter Class Classification Card - Solo Leveling Style */}
        <div 
          className="relative overflow-hidden bg-gradient-to-b from-slate-900/60 via-black to-slate-900/60 rounded-3xl p-4 sm:p-6 mb-6 shadow-2xl transition-all border-t-2 border-l-2 border-slate-600/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/80"
          style={{ 
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.7), 0 2px 8px rgba(0, 0, 0, 0.5), inset 0 2px 4px rgba(59, 130, 246, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.6)' 
          }}
        >
          
          {/* Angular Corner Accents */}
          <div className="absolute top-0 left-0 w-12 h-12 sm:w-16 sm:h-16 border-l-2 border-t-2 border-system-blue opacity-50"></div>
          <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 border-r-2 border-t-2 border-system-blue opacity-50"></div>
          <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-16 sm:h-16 border-l-2 border-b-2 border-system-blue opacity-50"></div>
          <div className="absolute bottom-0 right-0 w-12 h-12 sm:w-16 sm:h-16 border-r-2 border-b-2 border-system-blue opacity-50"></div>

          {/* Rank Badge - User's Actual Rank */}
          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-black border-2 border-system-blue w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center"
               style={{ 
                 clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                 boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), inset 0 0 20px rgba(59, 130, 246, 0.2)'
               }}>
            <div className="flex flex-col items-center">
              <span className="font-orbitron font-black text-[10px] sm:text-xs text-gray-400 leading-none">RANK</span>
              <span className="font-orbitron font-black text-xl sm:text-2xl text-system-blue leading-none mt-0.5 sm:mt-1"
                    style={{ textShadow: '0 0 10px rgba(59, 130, 246, 0.8)' }}>
                {userRank}
              </span>
            </div>
          </div>

          {/* Class Info */}
          <div className="relative z-10">
            {/* Header Section */}
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-system-blue/20">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-system-blue/10 border border-system-blue/30 rounded"
                   style={{ boxShadow: '0 0 15px rgba(59, 130, 246, 0.3)' }}>
                <span className="text-3xl sm:text-4xl filter drop-shadow-lg">{hunterClass.icon}</span>
              </div>
              <div className="flex-1">
                <div className="text-[9px] sm:text-[10px] font-rajdhani uppercase text-system-blue tracking-[0.2em] mb-0.5 sm:mb-1 flex items-center gap-2">
                  <div className="w-1 h-1 bg-system-blue rounded-full animate-pulse"></div>
                  HUNTER CLASSIFICATION SYSTEM
                </div>
                <h4 className="text-xl sm:text-2xl font-orbitron font-black text-white tracking-wider"
                    style={{ textShadow: '0 0 20px rgba(59, 130, 246, 0.5)' }}>
                  {hunterClass.name}
                </h4>
              </div>
            </div>
            
            {/* Description Section */}
            <div className="space-y-2.5 sm:space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-1 h-3.5 sm:h-4 bg-system-blue"></div>
                <p className="text-[13px] sm:text-sm font-rajdhani text-system-blue italic font-medium">
                  「 {hunterClass.title} 」
                </p>
              </div>
              
              <p className="text-[13px] sm:text-sm font-rajdhani text-gray-300 leading-relaxed pl-3 border-l-2 border-system-blue/30">
                {hunterClass.description}
              </p>
              
              <div className="pt-2.5 sm:pt-3 mt-2.5 sm:mt-3 border-t border-system-blue/20 flex items-start gap-2 pl-3">
                <span className="text-system-blue text-[11px] sm:text-xs mt-0.5">▶</span>
                <p className="text-[11px] sm:text-xs font-rajdhani text-gray-400 leading-relaxed">
                  {classMotivation}
                </p>
              </div>
            </div>
          </div>

          {/* Animated Scan Line Effect */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-system-blue/5 to-transparent h-32 animate-scan"></div>
          </div>

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>

        {/* Stats Radar Chart */}
        <div className="flex items-center justify-center">
          <ResponsiveContainer width="100%" height={isSmallScreen ? 230 : 300}>
            <RadarChart data={statsRadarData}>
              <defs>
                <linearGradient id="radarGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <PolarGrid stroke="#3B82F6" strokeOpacity={0.3} />
              <PolarAngleAxis 
                dataKey="stat" 
                stroke="#3B82F6"
                style={{ fontFamily: 'Orbitron', fontSize: isSmallScreen ? 12 : 14, fontWeight: 'bold', fill: '#60A5FA' }}
              />
              <PolarRadiusAxis angle={90} domain={[0, radarMaxDomain]} stroke="#3B82F6" strokeOpacity={0.5} />
              <Radar 
                name="Stats" 
                dataKey="value" 
                stroke="#3B82F6" 
                strokeWidth={isSmallScreen ? 2 : 3}
                fill="url(#radarGradient)"
                fillOpacity={0.7}
                dot={{ fill: '#3B82F6', r: isSmallScreen ? 4 : 6, strokeWidth: 2, stroke: '#fff' }}
              />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* XP Progress - Area Chart with Glow */}
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 hover:border-t-purple-400/70 hover:border-l-purple-400/70 hover:border-r-purple-600/80 hover:border-b-purple-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h3 className="text-base font-bold font-orbitron text-purple-400 mb-3 flex items-center gap-2">
            <span className="text-xl">⚡</span>
            <span>{t.stats.xpProgression}</span>
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="purpleGlow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A78BFA" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" opacity={0.3} />
              <XAxis dataKey="date" stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <YAxis stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="XP" 
                stroke="#A78BFA" 
                strokeWidth={3}
                fill="url(#purpleGlow)"
                dot={{ fill: '#A78BFA', r: 4, strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Quests Performance - Area Chart */}
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h3 className="text-base font-bold font-orbitron text-system-blue mb-3 flex items-center gap-2">
            <span className="text-xl">⚔️</span>
            <span>{t.stats.questActivity}</span>
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="blueGlow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#1E40AF" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" opacity={0.3} />
              <XAxis dataKey="date" stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <YAxis stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="Quests" 
                stroke="#3B82F6" 
                strokeWidth={3}
                fill="url(#blueGlow)"
                dot={{ fill: '#3B82F6', r: 4, strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Focus Time - Line Chart with Glow */}
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 hover:border-t-yellow-400/70 hover:border-l-yellow-400/70 hover:border-r-yellow-600/80 hover:border-b-yellow-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h3 className="text-base font-bold font-orbitron text-yellow-400 mb-3 flex items-center gap-2">
            <span className="text-xl">🎯</span>
            <span>{t.stats.focusDuration}</span>
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" opacity={0.3} />
              <XAxis dataKey="date" stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <YAxis stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="Focus Time" 
                stroke="#FBBF24" 
                strokeWidth={4}
                dot={{ fill: '#FBBF24', r: 5, strokeWidth: 2, stroke: '#fff', filter: 'url(#glow)' }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Currency Growth - Area Chart */}
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 hover:border-t-green-400/70 hover:border-l-green-400/70 hover:border-r-green-600/80 hover:border-b-green-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h3 className="text-base font-bold font-orbitron mb-3 flex items-center gap-2" style={{ color: '#FCD34D' }}>
            <span className="text-xl">💎</span>
            <span>{t.stats.wealthAccumulation}</span>
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="goldGlow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FCD34D" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" opacity={0.3} />
              <XAxis dataKey="date" stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <YAxis stroke="#94A3B8" style={{ fontFamily: 'Rajdhani', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="Currency" 
                stroke="#FCD34D" 
                strokeWidth={3}
                fill="url(#goldGlow)"
                dot={{ fill: '#FCD34D', r: 4, strokeWidth: 2, stroke: '#fff' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;

